import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { useDsChatQueue } from 'hooks/useDsChatQueue';
import { useSceneChatQueue } from 'hooks/useSceneChatQueue';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import { DefaultTheme, useTheme } from 'styled-components';
import * as S from './styles';

interface Props {
	chatType: 'ds' | 'scene';
	chatId: string | null;
}

interface QueueItemProps {
	entry: { id: string; label: string; payload?: any };
	attachmentCount: number;
	tooltipId: string;
	theme: DefaultTheme;
	onCancel: (id: string) => void;
}

const QueueItem = ({ entry, attachmentCount, tooltipId, theme, onCancel }: QueueItemProps) => {
	const labelRef = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	// Tooltip only fires when the label is actually clipped by ellipsis.
	// Re-checks on element resize so the truncation state stays accurate.
	useLayoutEffect(() => {
		const el = labelRef.current;
		if (!el) return;
		const check = () => setIsTruncated(el.scrollWidth > el.clientWidth);
		check();
		const ro = new ResizeObserver(check);
		ro.observe(el);
		return () => ro.disconnect();
	}, [entry.label]);

	const tooltipProps = isTruncated
		? { 'data-tooltip-id': tooltipId, 'data-tooltip-content': entry.label }
		: {};

	return (
		<S.Item {...tooltipProps}>
			<S.ItemLabel ref={labelRef}>{entry.label}</S.ItemLabel>
			{attachmentCount > 0 && (
				<S.AttachmentBadge>
					<Icon type={IconType.ATTACHMENT} width={11} height={11} color={theme.gray[600]} />
					{attachmentCount}
				</S.AttachmentBadge>
			)}
			<S.CancelButton
				onClick={() => onCancel(entry.id)}
				aria-label={`Cancel queued ${entry.label}`}
			>
				<Icon type={IconType.CLOSE} width={10} height={10} color={theme.gray[600]} />
			</S.CancelButton>
		</S.Item>
	);
};

const attachmentCountFor = (entry: { payload?: any }): number => {
	const p = entry.payload;
	if (p?.kind === 'addUserEntry') return p.entry?.attachments?.length ?? 0;
	return 0;
};

const QueueIndicator = ({ chatType, chatId }: Props) => {
	const theme = useTheme();
	const tooltipId = useId();
	// Both hooks are always called (rules of hooks). The unused one is a no-op
	// because we pass null for its chatId.
	const dsQueue = useDsChatQueue(chatType === 'ds' ? chatId : null);
	const sceneQueue = useSceneChatQueue(chatType === 'scene' ? chatId : null);

	// Filter out entries that are currently firing (in-flight). Those aren't
	// "queued" from the user's perspective — they're already being processed.
	const allItems = chatType === 'ds' ? dsQueue.items : sceneQueue.items;
	const items = allItems.filter((e) => !e.firing);
	const cancel = chatType === 'ds' ? dsQueue.cancel : sceneQueue.cancel;

	if (items.length === 0) return null;

	return (
		<S.Base>
			<S.Header>
				<S.HeaderIcon>
					<Icon type={IconType.TIME} width={12} height={12} color={theme.gray[700]} />
				</S.HeaderIcon>
				{items.length} QUEUED
			</S.Header>
			<S.Items>
				{items.map((entry) => (
					<QueueItem
						key={entry.id}
						entry={entry}
						attachmentCount={attachmentCountFor(entry)}
						tooltipId={tooltipId}
						theme={theme}
						onCancel={cancel}
					/>
				))}
			</S.Items>
			<Tooltip id={tooltipId} placement="top" />
		</S.Base>
	);
};

export default QueueIndicator;
